import { PaginationRequest } from '@monorepo/types';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import {
  fetchPurchaseInvoice,
  fetchPurchaseMetrics,
  fetchPurchases,
  fetchPurchaseStatus,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { ExtractFnReturnType } from '@hub/utils/react-query/react-query';
import { useQuery } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.PURCHASE;

export const usePurchases = (paginationRequest: PaginationRequest) => {
  return useQuery<ExtractFnReturnType<typeof fetchPurchases>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].LIST(paginationRequest),
    queryFn: () => fetchPurchases(paginationRequest),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const usePurchaseInvoice = (purchaseId: string) => {
  return useQuery<ExtractFnReturnType<typeof fetchPurchaseInvoice>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].INVOICE(purchaseId),
    queryFn: () => fetchPurchaseInvoice(purchaseId),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    enabled: !!purchaseId,
  });
};

export const usePurchaseStatus = (snapshotId: string, enabled = true) => {
  return useQuery<ExtractFnReturnType<typeof fetchPurchaseStatus>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].STATUS(snapshotId),
    queryFn: () => fetchPurchaseStatus(snapshotId),
    staleTime: 0, // Always refetch for status updates
    gcTime: 0, // Don't cache status results
    refetchInterval: enabled ? 2000 : false, // Poll every 2 seconds when enabled
    refetchIntervalInBackground: false, // Don't poll when tab is not active
    refetchOnWindowFocus: true, // Refetch when user comes back to tab
    enabled: enabled,
  });
};

export const usePurchaseMetrics = () => {
  return useQuery<ExtractFnReturnType<typeof fetchPurchaseMetrics>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].METRICS,
    queryFn: () => fetchPurchaseMetrics(),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};
